"use client";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
} from "@pex/ui-web";
import { Loader2, MapPin, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useMap } from "../context";
import { useDebounce } from "../hooks/use-debounce";
import type { LocationFeature, LocationSuggestion } from "../types";
import { iconMap } from "../utils/icons";

interface MapSearchProps {
  /**
   * Mapbox access token. Falls back to NEXT_PUBLIC_MAPBOX_TOKEN
   * environment variable if not provided.
   */
  accessToken?: string;
  /**
   * ISO 3166-1 alpha-2 country code (comma-separated for several) to restrict
   * results. Omitted by default so locations can be searched worldwide.
   */
  country?: string;
  /** Maximum number of suggestions to show. Defaults to 5. */
  limit?: number;
  /** Called when the user clears the search input. */
  onLocationClear?: () => void;
  /**
   * Called when the user selects a location from results.
   * The map automatically flies to the selection.
   */
  onLocationSelect?: (feature: LocationFeature) => void;
  /** Input placeholder text. */
  placeholder?: string;
  /**
   * [longitude, latitude] bias point for ranking nearby results.
   * Defaults to central South Africa.
   */
  proximity?: [number, number];
  /**
   * Mapbox SearchBox session token. Groups suggest + retrieve calls into a
   * single billable session. When provided the consuming app controls rotation
   * (e.g. per user session or search intent). When omitted a UUID is generated
   * per component mount and rotated automatically after each completed retrieval.
   */
  sessionToken?: string;
}

export function MapSearch({
  onLocationSelect,
  onLocationClear,
  country,
  proximity = [25.0, -29.0],
  limit = 5,
  placeholder = "Search locations...",
  accessToken,
  sessionToken: sessionTokenProp,
}: MapSearchProps) {
  const { map } = useMap();
  const [query, setQuery] = useState("");
  const [displayValue, setDisplayValue] = useState("");
  const [results, setResults] = useState<LocationSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 400);

  // Internal session token used when the consumer does not provide one.
  // Rotated after each completed retrieval to match Mapbox billing expectations.
  const internalSessionToken = useRef(crypto.randomUUID());
  const componentId = useId();

  const activeSessionToken = sessionTokenProp ?? internalSessionToken.current;

  const token = accessToken ?? process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? "";

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const searchLocations = async () => {
      setIsSearching(true);
      setIsOpen(true);

      try {
        const params = new URLSearchParams({
          q: debouncedQuery,
          access_token: token,
          session_token: activeSessionToken,
          limit: String(limit),
          // Ranking bias only — results from anywhere are still returned.
          proximity: proximity.join(","),
        });
        // Only sent when a caller explicitly restricts the search; omitting the
        // param entirely is what lets Mapbox return worldwide results.
        if (country) {
          params.set("country", country);
        }

        const res = await fetch(
          `https://api.mapbox.com/search/searchbox/v1/suggest?${params}`
        );
        const data = await res.json();
        setResults(data.suggestions ?? []);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    searchLocations();
  }, [debouncedQuery, token, country, limit, proximity, activeSessionToken]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setDisplayValue(value);
  };

  const handleSelect = async (suggestion: LocationSuggestion) => {
    setIsSearching(true);

    try {
      const params = new URLSearchParams({
        access_token: token,
        session_token: activeSessionToken,
      });

      const res = await fetch(
        `https://api.mapbox.com/search/searchbox/v1/retrieve/${suggestion.mapbox_id}?${params}`
      );
      const data = await res.json();
      const features: LocationFeature[] = data?.features ?? [];

      if (map && features.length > 0) {
        const first = features[0];
        if (!first) {
          return;
        }
        const [lng, lat] = first.geometry.coordinates;

        map.flyTo({
          center: [lng, lat],
          zoom: 14,
          speed: 4,
          duration: 1000,
          essential: true,
        });

        setDisplayValue(suggestion.name);
        setResults([]);
        setIsOpen(false);

        // Only rotate internal token — consumer-provided tokens are managed externally
        if (!sessionTokenProp) {
          internalSessionToken.current = crypto.randomUUID();
        }

        onLocationSelect?.(first);
      }
    } catch {
      // retrieve failed silently
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setDisplayValue("");
    setResults([]);
    setIsOpen(false);
    onLocationClear?.();
  };

  return (
    <section
      aria-label="Location search"
      className="absolute top-4 left-1/2 z-10 w-[90vw] -translate-x-1/2 rounded-lg shadow-lg sm:left-4 sm:w-[350px] sm:translate-x-0"
    >
      <Command className="rounded-lg">
        <div
          className={cn(
            "flex w-full items-center justify-between gap-1 px-3",
            isOpen && "border-b"
          )}
        >
          <CommandInput
            className="flex-1"
            id={`${componentId}-search`}
            onValueChange={handleInputChange}
            placeholder={placeholder}
            value={displayValue}
          />
          {displayValue && !isSearching && (
            <X
              aria-label="Clear search"
              className="size-4 shrink-0 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
              onClick={clearSearch}
              role="button"
            />
          )}
          {isSearching && (
            <Loader2
              aria-label="Searching"
              className="size-4 shrink-0 animate-spin text-primary"
            />
          )}
        </div>

        {isOpen && (
          <CommandList className="max-h-60 overflow-y-auto">
            {!isSearching && results.length === 0 && (
              <CommandEmpty className="py-6 text-center">
                <div className="flex flex-col items-center justify-center space-y-1">
                  <p className="font-medium text-sm">No locations found</p>
                  <p className="text-muted-foreground text-xs">
                    Try a different search term
                  </p>
                </div>
              </CommandEmpty>
            )}
            {results.length > 0 && (
              <CommandGroup>
                {results.map((location) => (
                  <CommandItem
                    className="flex cursor-pointer items-center rounded-md px-2 py-3 hover:bg-accent"
                    key={location.mapbox_id}
                    onSelect={() => handleSelect(location)}
                    value={`${location.name} ${location.place_formatted} ${location.mapbox_id}`}
                  >
                    <div className="flex items-center space-x-2">
                      <div className="rounded-full bg-primary/10 p-1.5">
                        {location.maki && iconMap[location.maki] ? (
                          iconMap[location.maki]
                        ) : (
                          <MapPin className="h-4 w-4 text-primary" />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="max-w-[270px] truncate font-medium text-sm">
                          {location.name}
                        </span>
                        <span className="max-w-[270px] truncate text-muted-foreground text-xs">
                          {location.place_formatted}
                        </span>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        )}
      </Command>
    </section>
  );
}
